import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Pill, AlertTriangle, Activity, 
  BookOpen, Stethoscope, Users, Globe, FlaskConical,
  Shield, Heart, Baby, FileText, CheckCircle, XCircle,
  Info, Beaker, TrendingUp, DollarSign, Thermometer
, AlertCircle} from 'lucide-react';
import { getDrugBySlug } from '../data/enhancedMedicines';

const DrugDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const drug = slug ? getDrugBySlug(slug) : undefined;

  if (!drug) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Pill className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Drug Not Found</h2>
          <p className="text-gray-500 mb-4">The requested medication could not be found.</p>
          <Link to="/drugs" className="text-teal-600 hover:text-teal-700 flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Drug Database
          </Link>
        </div>
      </div>
    );
  }

  const getCostColor = (affordability?: string) => {
    if (!affordability) return 'bg-gray-500';
    const lower = affordability.toLowerCase();
    if (lower.includes('very affordable') || lower.includes('low cost')) return 'bg-green-500';
    if (lower.includes('affordable') || lower.includes('moderate')) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getCostLabel = (affordability?: string) => {
    if (!affordability) return 'Unknown';
    const lower = affordability.toLowerCase();
    if (lower.includes('very affordable') || lower.includes('low cost')) return 'Low';
    if (lower.includes('affordable') || lower.includes('moderate')) return 'Moderate';
    return 'High';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/drugs" className="inline-flex items-center text-teal-100 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Drug Database
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Pill className="w-8 h-8" />
                <h1 className="text-3xl font-bold">{drug.genericName}</h1>
                <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  WHO Essential
                </span>
              </div>
              <p className="text-teal-100 text-lg">{drug.therapeuticClass}</p>
              {drug.brandNames && drug.brandNames.length > 0 && (
                <p className="text-teal-200 text-sm mt-1">
                  Brand names: {drug.brandNames.slice(0, 5).join(', ')}
                  {drug.brandNames.length > 5 && ` +${drug.brandNames.length - 5} more`}
                </p>
              )}
              {drug.atcCode && (
                <p className="text-teal-200 text-sm mt-1">ATC Code: {drug.atcCode}</p>
              )}
            </div>
            
            <div className={`${getCostColor(drug.africanContext?.affordability)} text-white px-4 py-2 rounded-lg text-center`}>
              <div className="text-xs uppercase tracking-wide">Cost</div>
              <div className="text-lg font-bold">{getCostLabel(drug.africanContext?.affordability)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
            {/* Medical Disclaimer - Prominent Alert */}
            {drug.medicalDisclaimer && (
              <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-800 text-sm uppercase tracking-wide mb-1">
                      Clinical Disclaimer
                    </h3>
                    <p className="text-amber-900 text-sm leading-relaxed">
                      {drug.medicalDisclaimer.replace('CLINICAL DISCLAIMER: ', '')}
                    </p>
                  </div>
                </div>
              </div>
            )}

<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Indications */}
            {drug.indications && drug.indications.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-teal-600" />
                  Indications
                </h2>
                <div className="space-y-3">
                  {drug.indications.map((indication, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${indication.firstLine ? 'text-green-500' : 'text-blue-500'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-800">{indication.condition}</span>
                          {indication.firstLine && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">First-line</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            indication.recommendationStrength === 'Strong' ? 'bg-green-100 text-green-800' :
                            indication.recommendationStrength === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {indication.recommendationStrength}
                          </span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            Evidence: {indication.evidenceLevel}
                          </span>
                        </div>
                        {indication.notes && (
                          <p className="text-sm text-gray-600 mt-1">{indication.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dosing */}
            {drug.dosing && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-teal-600" />
                  Dosing
                </h2>
                
                {/* Adult Dosing */}
                {drug.dosing.adults && (
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Adult Dosing
                    </h3>
                    <div className="bg-teal-50 border-l-4 border-teal-500 p-4 mb-3">
                      <p className="font-medium text-teal-800">Standard: {drug.dosing.adults.standard}</p>
                      {drug.dosing.adults.maxDose && (
                        <p className="text-sm text-teal-700 mt-1">Max dose: {drug.dosing.adults.maxDose}</p>
                      )}
                    </div>
                    {drug.dosing.adults.indicationSpecific && drug.dosing.adults.indicationSpecific.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <h4 className="text-sm font-medium text-gray-600">Indication-Specific Dosing:</h4>
                        {drug.dosing.adults.indicationSpecific.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                            <span className="text-gray-700">{item.indication}</span>
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{item.dose}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Pediatric Dosing */}
                {drug.dosing.pediatric && (
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Baby className="w-4 h-4" />
                      Pediatric Dosing
                    </h3>
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-3">
                      <p className="font-medium text-blue-800">{drug.dosing.pediatric.standard}</p>
                      {drug.dosing.pediatric.weightBased && (
                        <p className="text-sm text-blue-700 mt-1">Weight-based dosing</p>
                      )}
                      {drug.dosing.pediatric.maxDose && (
                        <p className="text-sm text-blue-700 mt-1">Max dose: {drug.dosing.pediatric.maxDose}</p>
                      )}
                    </div>
                    {drug.dosing.pediatric.neonatal && (
                      <div className="bg-purple-50 border-l-4 border-purple-500 p-3 mb-3">
                        <p className="text-sm text-purple-800"><strong>Neonatal:</strong> {drug.dosing.pediatric.neonatal}</p>
                      </div>
                    )}
                    {drug.dosing.pediatric.ageSpecific && drug.dosing.pediatric.ageSpecific.length > 0 && (
                      <div className="space-y-2">
                        {drug.dosing.pediatric.ageSpecific.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                            <span className="text-gray-700">{item.age}</span>
                            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{item.dose}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Elderly */}
                {drug.dosing.elderly && (
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-700 mb-2">Elderly</h3>
                    <p className="text-gray-600 text-sm">{drug.dosing.elderly}</p>
                  </div>
                )}

                {/* Renal Impairment */}
                {drug.dosing.adults?.renalImpairment && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <h3 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Renal Impairment Dosing
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><strong>Mild:</strong> {drug.dosing.adults.renalImpairment.mild}</div>
                      <div><strong>Moderate:</strong> {drug.dosing.adults.renalImpairment.moderate}</div>
                      <div><strong>Severe:</strong> {drug.dosing.adults.renalImpairment.severe}</div>
                      <div><strong>Dialysis:</strong> {drug.dosing.adults.renalImpairment.dialysis}</div>
                    </div>
                  </div>
                )}

                {/* Hepatic Impairment */}
                {drug.dosing.adults?.hepaticImpairment && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <h3 className="font-medium text-orange-800 mb-2">Hepatic Impairment</h3>
                    <p className="text-sm text-orange-700">{drug.dosing.adults.hepaticImpairment}</p>
                  </div>
                )}

                {/* Routes & Administration */}
                {drug.dosing.routes && drug.dosing.routes.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="text-sm text-gray-600">Routes:</span>
                    {drug.dosing.routes.map((route, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{route}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pharmacology */}
            {drug.pharmacology && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-teal-600" />
                  Pharmacology
                </h2>
                
                {drug.pharmacology.mechanism && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Mechanism of Action</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{drug.pharmacology.mechanism}</p>
                  </div>
                )}

                {drug.pharmacology.pharmacodynamics && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Pharmacodynamics</h3>
                    <p className="text-gray-600 text-sm">{drug.pharmacology.pharmacodynamics}</p>
                  </div>
                )}

                {drug.pharmacology.pharmacokinetics && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700">Absorption</h4>
                      <p className="text-sm text-gray-600 mt-1">{drug.pharmacology.pharmacokinetics.absorption}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700">Distribution</h4>
                      <p className="text-sm text-gray-600 mt-1">{drug.pharmacology.pharmacokinetics.distribution}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700">Metabolism</h4>
                      <p className="text-sm text-gray-600 mt-1">{drug.pharmacology.pharmacokinetics.metabolism}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700">Elimination</h4>
                      <p className="text-sm text-gray-600 mt-1">{drug.pharmacology.pharmacokinetics.elimination}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                      <h4 className="text-sm font-medium text-gray-700">Half-Life</h4>
                      <p className="text-sm text-gray-600 mt-1">{drug.pharmacology.pharmacokinetics.halfLife}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Drug Interactions */}
            {drug.interactions && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Beaker className="w-5 h-5 text-teal-600" />
                  Drug Interactions
                </h2>
                
                {drug.interactions.major && drug.interactions.major.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-red-700 mb-2">Major Interactions</h3>
                    <div className="space-y-2">
                      {drug.interactions.major.map((interaction, idx) => (
                        <div key={idx} className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg">
                          <div className="font-medium text-red-800">{interaction.drug}</div>
                          <p className="text-sm text-red-700 mt-1">{interaction.effect}</p>
                          {interaction.mechanism && (
                            <p className="text-xs text-red-600 mt-1"><em>Mechanism:</em> {interaction.mechanism}</p>
                          )}
                          {interaction.management && (
                            <p className="text-sm text-red-600 mt-1"><strong>Management:</strong> {interaction.management}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drug.interactions.moderate && drug.interactions.moderate.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-amber-700 mb-2">Moderate Interactions</h3>
                    <div className="space-y-2">
                      {drug.interactions.moderate.map((interaction, idx) => (
                        <div key={idx} className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-lg">
                          <div className="font-medium text-amber-800">{interaction.drug}</div>
                          <p className="text-sm text-amber-700 mt-1">{interaction.effect}</p>
                          {interaction.management && (
                            <p className="text-sm text-amber-600 mt-1"><strong>Management:</strong> {interaction.management}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drug.interactions.food && drug.interactions.food.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Food Interactions</h3>
                    <ul className="space-y-1">
                      {drug.interactions.food.map((food, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <Info className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          {food}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Clinical Pearls */}
            {drug.clinicalPearls && drug.clinicalPearls.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  Clinical Pearls
                </h2>
                <ul className="space-y-2">
                  {drug.clinicalPearls.map((pearl, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{pearl}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Patient Counseling */}
            {drug.patientCounseling && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-teal-600" />
                  Patient Counseling
                </h2>
                
                {drug.patientCounseling.keyPoints && drug.patientCounseling.keyPoints.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Key Points</h3>
                    <ul className="space-y-1">
                      {drug.patientCounseling.keyPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.patientCounseling.commonMisconceptions && drug.patientCounseling.commonMisconceptions.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Common Misconceptions</h3>
                    <ul className="space-y-1">
                      {drug.patientCounseling.commonMisconceptions.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.patientCounseling.adherenceTips && drug.patientCounseling.adherenceTips.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-medium text-gray-700 mb-2">Adherence Tips</h3>
                    <ul className="space-y-1">
                      {drug.patientCounseling.adherenceTips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.patientCounseling.warningSignsToReport && drug.patientCounseling.warningSignsToReport.length > 0 && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h3 className="font-medium text-red-800 mb-2">Warning Signs to Report</h3>
                    <ul className="space-y-1">
                      {drug.patientCounseling.warningSignsToReport.map((sign, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {sign}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            
            {/* Contraindications */}
            {drug.contraindications && (
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h2 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Contraindications
                </h2>
                
                {drug.contraindications.absolute && drug.contraindications.absolute.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-red-700 mb-1">Absolute</h3>
                    <ul className="space-y-1">
                      {drug.contraindications.absolute.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-red-700 text-sm">
                          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.contraindications.relative && drug.contraindications.relative.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-amber-700 mb-1">Relative</h3>
                    <ul className="space-y-1">
                      {drug.contraindications.relative.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-amber-700 text-sm">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.contraindications.precautions && drug.contraindications.precautions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Precautions</h3>
                    <ul className="space-y-1">
                      {drug.contraindications.precautions.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* African Context */}
            {drug.africanContext && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-teal-600" />
                  African Context
                </h2>
                
                {drug.africanContext.availability && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Availability</h3>
                    <p className="text-sm text-gray-600">{drug.africanContext.availability}</p>
                  </div>
                )}

                {drug.africanContext.affordability && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      Affordability
                    </h3>
                    <p className="text-sm text-gray-600">{drug.africanContext.affordability}</p>
                  </div>
                )}

                {drug.africanContext.resistancePatterns && (
                  <div className="mb-4 bg-amber-50 p-3 rounded-lg">
                    <h3 className="text-sm font-medium text-amber-800 mb-1 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      Resistance Patterns
                    </h3>
                    <p className="text-sm text-amber-700">{drug.africanContext.resistancePatterns}</p>
                  </div>
                )}

                {drug.africanContext.endemicConsiderations && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Endemic Considerations</h3>
                    <p className="text-sm text-gray-600">{drug.africanContext.endemicConsiderations}</p>
                  </div>
                )}

                {drug.africanContext.supplyChainNotes && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Supply Chain</h3>
                    <p className="text-sm text-gray-600">{drug.africanContext.supplyChainNotes}</p>
                  </div>
                )}

                {drug.africanContext.localManufacturing && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Local Manufacturing</h3>
                    <p className="text-sm text-gray-600">{drug.africanContext.localManufacturing}</p>
                  </div>
                )}

                {drug.africanContext.alternatives && drug.africanContext.alternatives.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Alternatives</h3>
                    <ul className="space-y-1">
                      {drug.africanContext.alternatives.map((alt, idx) => (
                        <li key={idx} className="text-sm text-gray-600">• {alt}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Adverse Effects */}
            {drug.adverseEffects && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  Adverse Effects
                </h2>
                
                {drug.adverseEffects.common && drug.adverseEffects.common.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Common</h3>
                    <div className="space-y-2">
                      {drug.adverseEffects.common.map((effect, idx) => (
                        <div key={idx} className="bg-gray-50 p-2 rounded text-sm">
                          <strong>{effect.effect}</strong>
                          <span className="text-gray-500"> ({effect.frequency})</span>
                          {effect.management && (
                            <p className="text-xs text-gray-500 mt-1">Management: {effect.management}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drug.adverseEffects.serious && drug.adverseEffects.serious.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-red-700 mb-2">Serious</h3>
                    <div className="space-y-2">
                      {drug.adverseEffects.serious.map((effect, idx) => (
                        <div key={idx} className="bg-red-50 p-2 rounded text-sm border-l-2 border-red-400">
                          <strong className="text-red-800">{effect.effect}</strong>
                          <span className="text-red-600"> ({effect.frequency})</span>
                          {effect.warning && (
                            <p className="text-xs text-red-600 mt-1 font-medium">{effect.warning}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drug.adverseEffects.rare && drug.adverseEffects.rare.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Rare</h3>
                    <div className="flex flex-wrap gap-1">
                      {drug.adverseEffects.rare.map((effect, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{effect}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pregnancy & Lactation */}
            {drug.pregnancy && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Pregnancy & Lactation
                </h2>
                
                {drug.pregnancy.category && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-700">Category: </span>
                    <span className={`text-sm px-2 py-0.5 rounded ${
                      drug.pregnancy.category === 'A' ? 'bg-green-100 text-green-800' :
                      drug.pregnancy.category === 'B' ? 'bg-blue-100 text-blue-800' :
                      drug.pregnancy.category === 'C' ? 'bg-yellow-100 text-yellow-800' :
                      drug.pregnancy.category === 'D' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {drug.pregnancy.category}
                    </span>
                  </div>
                )}

                {drug.pregnancy.recommendation && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Recommendation</h3>
                    <p className="text-sm text-gray-600">{drug.pregnancy.recommendation}</p>
                  </div>
                )}

                {drug.pregnancy.trimpiesterRisks && (
                  <div className="mb-3 bg-pink-50 p-3 rounded-lg">
                    <h3 className="text-sm font-medium text-pink-800 mb-2">Trimester Risks</h3>
                    <div className="text-xs space-y-1">
                      <p><strong>1st:</strong> {drug.pregnancy.trimpiesterRisks.first}</p>
                      <p><strong>2nd:</strong> {drug.pregnancy.trimpiesterRisks.second}</p>
                      <p><strong>3rd:</strong> {drug.pregnancy.trimpiesterRisks.third}</p>
                    </div>
                  </div>
                )}

                {drug.pregnancy.lactation && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Lactation</h3>
                    <p className={`text-sm ${drug.pregnancy.lactation.safe ? 'text-green-600' : 'text-red-600'}`}>
                      {drug.pregnancy.lactation.safe ? '✓ Generally safe' : '✗ Use with caution'}
                    </p>
                    {drug.pregnancy.lactation.notes && (
                      <p className="text-xs text-gray-500 mt-1">{drug.pregnancy.lactation.notes}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Monitoring */}
            {drug.monitoring && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-teal-600" />
                  Monitoring
                </h2>
                
                {drug.monitoring.baseline && drug.monitoring.baseline.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Baseline</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {drug.monitoring.baseline.map((item, idx) => (
                        <li key={idx}>• {item.test} <span className="text-gray-400">({item.reason})</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.monitoring.ongoing && drug.monitoring.ongoing.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Ongoing</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {drug.monitoring.ongoing.map((item, idx) => (
                        <li key={idx}>• {item.test} <span className="text-gray-400">({item.frequency})</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {drug.monitoring.therapeutic && (drug.monitoring.therapeutic.range || drug.monitoring.therapeutic.timing) && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">Therapeutic Monitoring</h3>
                    {drug.monitoring.therapeutic.range && (
                      <p className="text-sm text-blue-700">Range: {drug.monitoring.therapeutic.range}</p>
                    )}
                    {drug.monitoring.therapeutic.timing && (
                      <p className="text-sm text-blue-700">Timing: {drug.monitoring.therapeutic.timing}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Storage */}
            {drug.storage && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-teal-600" />
                  Storage
                </h2>
                <div className="text-sm space-y-2">
                  <p><strong>Temperature:</strong> {drug.storage.temperature}</p>
                  <p><strong>Light:</strong> {drug.storage.light}</p>
                  <p><strong>Humidity:</strong> {drug.storage.humidity}</p>
                  {drug.storage.specialInstructions && (
                    <p><strong>Special:</strong> {drug.storage.specialInstructions}</p>
                  )}
                  <p><strong>Shelf Life:</strong> {drug.storage.shelfLife}</p>
                </div>
              </div>
            )}

            {/* References */}
            {drug.references && drug.references.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  References
                </h2>
                <ul className="space-y-1 text-sm text-gray-600">
                  {drug.references.map((ref, idx) => (
                    <li key={idx}>• {ref}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Last Updated */}
            {drug.lastUpdated && (
              <div className="text-center text-sm text-gray-500">
                Last updated: {drug.lastUpdated}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrugDetailPage;
